import { useCallback, useEffect } from 'react';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DEFAULT_DATE_FORMAT } from '../../../common/constants/state';
import {
	useAdvancedOptions,
	useImportType,
	useMappingState,
	useMigrationId,
	useProjectData,
	useSource,
	useUploadedConfigName,
} from '../../../controllers/import-state/selectors';
import { useReadCSVPreview } from '../../../services/read-csv';

export const useMappings = () => {
	const [source] = useSource();
	const [serviceName] = useImportType();
	const [uploadedConfigName] = useUploadedConfigName();
	const { isLoading, hasErrorOccurred, errorCode, readCSVPreview } = useReadCSVPreview(
		source,
		serviceName,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [projectData] = useProjectData();

	const [migrationId] = useMigrationId();
	const [
		mappingState,
		{ setMappingState, setMappingStateValidationError, setUnmappedColumnsNotIncluded },
	] = useMappingState();
	const [advancedOptions, { updateAdvancedOptions }] = useAdvancedOptions();
	const hasConfigFile = !!uploadedConfigName;

	const onReadCSV = useCallback(() => {
		if (migrationId !== null && mappingState === null) {
			readCSVPreview({
				migrationId,
				advancedOptions,
				projectData,
				hasConfigFile,
			}).then((resp) => {
				if (!resp) {
					setMappingState(null);
					return;
				}

				const { suggestedDateFormat, ...state } = resp;
				setMappingState(state);

				updateAdvancedOptions({
					dateFormat: suggestedDateFormat || DEFAULT_DATE_FORMAT,
				});

				fireTrackAnalytics(createAnalyticsEvent({}), 'csvFile read', {
					numberOfColumns: Object.keys(state.columns).length,
					numberOfMappedJiraFields: state.mappedJiraFieldIds.size,
					numberOfRows: state.rows.length,
					projectType: projectData.type,
					projectStyle: projectData.style,
					accessLevel: projectData.accessLevel,
				});
			});
		}
	}, [
		migrationId,
		mappingState,
		readCSVPreview,
		advancedOptions,
		projectData,
		hasConfigFile,
		setMappingState,
		updateAdvancedOptions,
		createAnalyticsEvent,
	]);

	useEffect(() => {
		onReadCSV();
	}, [onReadCSV]);

	return {
		isLoading,
		hasErrorOccurred,
		mappingState,
		errorCode,
		onRefresh: onReadCSV,
		setMappingStateValidationError,
		setUnmappedColumnsNotIncluded,
		hasConfigFile,
	};
};
